/**
 * Where the person is signed in — security card on the Profile page, for clients and staff.
 *
 * Lists ONLY the sessions open right now, as facts: the browser and system, the network name
 * when it is known, when the session began and when it was last active. "This device" comes
 * from the server (the session id in the token), never from a cookie. Each other session can
 * be signed out, and all of them at once; the one in use is signed out with the normal
 * sign-out. There is no history of past sign-ins, no place and no address on this screen.
 *
 * "Don't recognise a session? Secure your account" does what the new-sign-in e-mail's "No"
 * does: every session ends, a new password is owed and a client's account is put on hold.
 */
import { useCallback, useEffect, useState } from 'react';
import authService, { type ActiveSession } from '../../services/auth.service';
import { useAuthStore } from '../../stores/authStore';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import { readerLocale } from '../../i18n/language';

function when(iso: string): string {
  return new Date(iso).toLocaleString(readerLocale(), { dateStyle: 'medium', timeStyle: 'short' });
}

export default function SignedInSessions(): JSX.Element {
  const role = useAuthStore((state) => state.user?.role);
  const [sessions, setSessions] = useState<ActiveSession[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      setSessions(await authService.listMySessions());
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not load your sessions.'));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function run(action: () => Promise<unknown>, failure: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, failure));
    } finally {
      setBusy(false);
    }
  }

  async function secure(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await authService.secureMyAccount();
      globalThis.location.assign('/login?reason=account_secured');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not secure your account. Please try again.'));
      setBusy(false);
    }
  }

  const others = (sessions ?? []).filter((s) => !s.is_current);
  const button = {
    border: `1px solid ${colors.border}`,
    color: colors.ink,
    opacity: busy ? 0.6 : 1,
  } as const;

  return (
    <section
      className="rounded-2xl p-6 max-w-2xl mt-6"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      aria-label="Where you're signed in"
    >
      <h2 className="text-base font-semibold" style={{ color: colors.ink }}>
        {"Where you're signed in"}
      </h2>
      <p className="text-sm mt-1" style={{ color: colors.muted }}>
        The sessions open on your account right now.
      </p>

      <ul className="mt-4 space-y-3">
        {(sessions ?? []).map((s) => (
          <li
            key={s.id}
            className="flex items-start justify-between gap-4 rounded-lg p-3"
            style={{ border: `1px solid ${colors.border}` }}
          >
            <div className="text-sm" style={{ color: colors.ink }}>
              <div className="font-medium">
                {s.device_label}
                {s.network_name ? ` · ${s.network_name}` : ''}
                {s.is_current && (
                  <span
                    className="ml-2 text-xs px-2 py-0.5 rounded-full"
                    style={{ background: colors.successBg, color: colors.success }}
                  >
                    This device
                  </span>
                )}
              </div>
              <div className="text-xs mt-1" style={{ color: colors.muted }}>
                <span>Signed in</span> {when(s.started_at)} · <span>Last active</span>{' '}
                {when(s.last_seen_at)}
              </div>
            </div>
            {!s.is_current && (
              <button
                type="button"
                disabled={busy}
                onClick={() => { void run(() => authService.endSession(s.id), 'Could not sign that session out.'); }}
                className="text-sm font-medium px-3 py-1.5 rounded-lg shrink-0"
                style={button}
              >
                Sign out
              </button>
            )}
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {others.length > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={() => { void run(() => authService.endOtherSessions(), 'Could not sign out your other sessions.'); }}
            className="text-sm font-medium px-3 py-1.5 rounded-lg"
            style={button}
          >
            Sign out of all other sessions
          </button>
        )}
        {!confirming && (
          <span className="text-sm" style={{ color: colors.muted }}>
            {"Don't recognise a session?"}{' '}
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="font-medium underline"
              style={{ color: colors.ink }}
            >
              Secure your account
            </button>
          </span>
        )}
      </div>

      {confirming && (
        <div
          className="mt-4 rounded-lg p-3 text-sm space-y-3"
          role="alertdialog"
          aria-label="Secure your account"
          style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
        >
          <p>
            This signs you out of every session, this one included, and you will be asked to
            choose a new password when you sign in again.
          </p>
          {role === 'client' && (
            <p>Withdrawals and account changes will be paused until our team has verified your identity.</p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => { void secure(); }}
              className="text-sm font-medium px-3 py-1.5 rounded-lg"
              style={{ background: colors.gold, color: colors.surface, opacity: busy ? 0.6 : 1 }}
            >
              Yes, secure my account
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirming(false)}
              className="text-sm px-3 py-1.5 rounded-lg"
              style={{ color: colors.muted }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div
          className="rounded-lg p-3 mt-3 text-sm"
          role="alert"
          style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}
        >
          {error}
        </div>
      )}
    </section>
  );
}
